import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Circle as CircleIcon,
  Message as MessageIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { notificationsAPI } from '../../services/api';

interface Notification {
  id: number;
  type: string;  // UPDATED: Changed to string to handle all backend types
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  data?: {
    conversation_id?: number;
    conversation_display_id?: number | string;
    user_id?: number;
    avatar_url?: string;
    escalation_reason?: string;
    escalation_trigger?: string;
    was_already_assigned?: boolean;
    notification_message?: string;
  };
}

const NotificationsDropdown: React.FC = () => {
  const navigate = useNavigate();  // ADDED: 2025-12-04 - Navigation hook
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // ADDED: 27/01/2026 - View all notifications dialog
  const [viewAllDialogOpen, setViewAllDialogOpen] = useState(false);  // ADD THIS
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);  // ADD THIS
  const [loadingAll, setLoadingAll] = useState(false);  // ADD THIS
  // END OF ADDED: 27/01/2026 - View all notifications dialog

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationsAPI.getNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      // Handle 422 errors specifically
      if (error.response?.status === 422) {
        console.error('Validation error:', error.response.data);
        // Don't show mock data on validation errors, just log
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      // For other errors, use mock data as fallback (only in development)
      if (process.env.NODE_ENV === 'development') {
        // Mock notifications for demo purposes
        const mockNotifications: Notification[] = [
          {
            id: 1,
            type: 'message',
            title: 'New message',
            message: 'John Doe sent a new message',
            read: false,
            created_at: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
            data: {
              conversation_id: 123,
              avatar_url: undefined
            }
          },
          {
            id: 2,
            type: 'assignment',
            title: 'Conversation assigned',
            message: 'A new conversation has been assigned to you',
            read: false,
            created_at: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
            data: {
              conversation_id: 124
            }
          },
          {
            id: 3,
            type: 'mention',
            title: 'You were mentioned',
            message: 'Sarah mentioned you in a conversation',
            read: true,
            created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            data: {
              conversation_id: 125,
              user_id: 456
            }
          },
          {
            id: 4,
            type: 'system',
            title: 'System update',
            message: 'New features are now available in your dashboard',
            read: true,
            created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
          }
        ];
        setNotifications(mockNotifications);
        setUnreadCount(mockNotifications.filter(n => !n.read).length);
      } else {
        console.error('Error fetching notifications:', error);
        setNotifications([]);
        setUnreadCount(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // ADDED: 27/01/2026 - View all notifications dialog
  const fetchAllNotifications = async () => {
    try {
      setLoadingAll(true);
      // Fetch more notifications (increase limit to 200 or fetch all)
      const data = await notificationsAPI.getNotifications({ limit: 200 });
      setAllNotifications(data.notifications || []);
    } catch (error: any) {
      console.error('Error fetching all notifications:', error);
      if (error.response?.status === 422) {
        console.error('Validation error:', error.response.data);
        setAllNotifications([]);
        return;
      }
      setAllNotifications([]);
    } finally {
      setLoadingAll(false);
    }
  };

  // Handle "View All" button click
  const handleViewAllClick = () => {
    setViewAllDialogOpen(true);
    fetchAllNotifications();
  };

  // END OF ADDED: 27/01/2026 - View all notifications dialog

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // For demo purposes, mark as read locally
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // For demo purposes, mark all as read locally
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    }
  };

  // UPDATED: 2025-12-04 - Handle notification clicks with navigation
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate based on notification type
    const conversationId = notification.data?.conversation_id || notification.data?.conversation_display_id;

    if (conversationId) {
      // Navigate to conversation
      // navigate(`/conversations/${conversationId}`);
      navigate(`/dashboard/conversations/${conversationId}`); // added on 27/01/2026
    } else if (notification.type === 'AGENT_REGISTRATION' || notification.type === 'PROFILE_UPDATE') {
      // Navigate to profile/settings
      // navigate('/settings/profile');
      navigate('/dashboard/settings/profile'); // added on 27/01/2026
    }

    handleClose();
  };

  const getNotificationIcon = (type: string) => {
    // Handle backend notification types (uppercase with underscores)
    const normalizedType = type.toLowerCase();

    if (normalizedType.includes('message')) {
      return <MessageIcon fontSize="small" />;
    } else if (normalizedType.includes('assignment') || normalizedType.includes('escalation')) {
      return <AssignmentIcon fontSize="small" />;
    } else if (normalizedType.includes('mention')) {
      return <PersonIcon fontSize="small" />;
    } else if (normalizedType.includes('sla')) {
      return <WarningIcon fontSize="small" />;
    } else if (normalizedType.includes('system')) {
      return <InfoIcon fontSize="small" />;
    } else {
      return <InfoIcon fontSize="small" />;
    }
  };

  const getNotificationColor = (type: string) => {
    const normalizedType = type.toLowerCase();

    if (normalizedType.includes('message')) {
      return '#4caf50';
    } else if (normalizedType.includes('escalation')) {
      return '#ff5722';  // Orange-red for urgent escalations
    } else if (normalizedType.includes('assignment')) {
      return '#2196f3';
    } else if (normalizedType.includes('mention')) {
      return '#ff9800';
    } else if (normalizedType.includes('sla')) {
      return '#f44336';  // Red for SLA alerts
    } else if (normalizedType.includes('system')) {
      return '#9c27b0';
    } else {
      return '#757575';
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            overflow: 'visible',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={markAllAsRead}>
                Mark all read
              </Button>
            )}
          </Box>
          {unreadCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 300, overflow: 'auto' }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: notification.read ? 'transparent' : '#f5f5f5',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar 
                      sx={{ 
                        bgcolor: getNotificationColor(notification.type),
                        width: 32,
                        height: 32
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {notification.title}
                        </Typography>
                        {!notification.read && (
                          <CircleIcon sx={{ fontSize: 8, color: '#2196f3' }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider variant="middle" />}
              </React.Fragment>
            ))}
          </List>
        )}

        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
              {/* <Button fullWidth variant="text" size="small"> */}
              <Button 
                fullWidth variant="text" 
                size="small" 
                onClick={handleViewAllClick}
                >
                View all notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
      {/* ADDED: 27/01/2026 - View all notifications dialog */}
      {/* View All Notifications Dialog */}
      <Dialog
        open={viewAllDialogOpen}
        onClose={() => setViewAllDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              All Notifications
            </Typography>
            <Box display="flex" gap={1}>
              {allNotifications.filter(n => !n.read).length > 0 && (
                <Button size="small" onClick={markAllAsRead}>
                  Mark all read
                </Button>
              )}
              <IconButton size="small" onClick={() => setViewAllDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          {loadingAll ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : allNotifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No notifications found
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {allNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    onClick={() => {
                      handleNotificationClick(notification);
                      setViewAllDialogOpen(false);
                    }}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: notification.read ? 'transparent' : '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        sx={{ 
                          bgcolor: getNotificationColor(notification.type),
                          width: 40,
                          height: 40
                        }}
                      >
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {notification.title}
                          </Typography>
                          {!notification.read && (
                            <CircleIcon sx={{ fontSize: 8, color: '#2196f3' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" mb={0.5}>
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < allNotifications.length - 1 && <Divider variant="middle" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setViewAllDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* END OF ADDED: 27/01/2026 - View all notifications dialog */}
    </>
  );
};

export default NotificationsDropdown;